<template>
  <article
    class="lesson-step-card"
    :class="{ 'lesson-step-card--seen': seen }"
    :aria-label="`${typeLabel} step ${stepIndex} of ${stepTotal}`"
  >
    <span class="lesson-step-card__rail" aria-hidden="true" />

    <div class="lesson-step-card__body">
      <div class="lesson-step-card__heading">
        <span class="lesson-step-card__chip" data-test="lesson-step-card-chip">
          <svg
            v-if="type === 'video'"
            class="lesson-step-card__chip-icon"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9.5 8.96533C9.5 8.48805 9.5 8.24941 9.59974 8.11618C9.68666 8.00007 9.81971 7.92744 9.96438 7.9171C10.1304 7.90525 10.3311 8.03429 10.7326 8.29239L15.4532 11.3271C15.8016 11.551 15.9758 11.663 16.0359 11.8054C16.0885 11.9298 16.0885 12.0702 16.0359 12.1946C15.9758 12.337 15.8016 12.449 15.4532 12.6729L10.7326 15.7076C10.3311 15.9657 10.1304 16.0948 9.96438 16.0829C9.81971 16.0726 9.68666 15.9999 9.59974 15.8838C9.5 15.7506 9.5 15.512 9.5 15.0347V8.96533Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else
            class="lesson-step-card__chip-icon"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M14 2.26953V6.40007C14 6.96012 14 7.24015 14.109 7.45406C14.2049 7.64222 14.3578 7.7952 14.546 7.89108C14.7599 8.00007 15.0399 8.00007 15.6 8.00007H19.7305M14 17H8M16 13H8M20 9.98822V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V6.8C4 5.11984 4 4.27976 4.32698 3.63803C4.6146 3.07354 5.07354 2.6146 5.63803 2.32698C6.27976 2 7.11984 2 8.8 2H12.0118C12.7455 2 13.1124 2 13.4577 2.08289C13.7638 2.15638 14.0564 2.27759 14.3249 2.44208C14.6276 2.6276 14.887 2.88703 15.4059 3.40589L18.5941 6.59411C19.113 7.11297 19.3724 7.3724 19.5579 7.67515C19.7224 7.94356 19.8436 8.2362 19.9171 8.5423C20 8.88757 20 9.25445 20 9.98822Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ typeLabel }}
        </span>
        <span class="lesson-step-card__step">Step {{ stepIndex }} of {{ stepTotal }}</span>
      </div>

      <div class="lesson-step-card__content">
        <slot />
      </div>
    </div>

    <footer class="lesson-step-card__footer">
      <span class="lesson-step-card__status" aria-live="polite" data-test="lesson-step-card-status">
        <svg
          v-if="seen"
          class="lesson-step-card__status-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ statusLabel }}
      </span>
    </footer>
  </article>
</template>

<script setup>
import { computed } from "vue";

const STATUS_LABELS = {
  video: { unseen: "Not yet watched", seen: "Video seen" },
  article: { unseen: "Not yet read", seen: "Article seen" },
};

const props = defineProps({
  type: {
    type: String,
    default: "video",
    validator: (value) => ["video", "article"].includes(value),
  },
  stepIndex: { type: Number, default: 1 },
  stepTotal: { type: Number, default: 1 },
  seen: { type: Boolean, default: false },
});

const typeLabel = computed(() => (props.type === "article" ? "Article" : "Video"));

const statusLabel = computed(() => {
  const labels = STATUS_LABELS[props.type];
  return props.seen ? labels.seen : labels.unseen;
});
</script>

<style scoped>
.lesson-step-card {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #ffffff;
  font-family: Inter, sans-serif;
}

.lesson-step-card__rail {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: #d0d5dd;
}

.lesson-step-card__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 16px 16px 16px 20px;
  background: #ffffff;
}

.lesson-step-card__heading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.lesson-step-card__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px 2px 8px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #f9fafb;
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.lesson-step-card__chip-icon {
  flex-shrink: 0;
}

.lesson-step-card__step {
  color: #475467;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.lesson-step-card__content {
  min-width: 0;
}

.lesson-step-card__footer {
  display: flex;
  align-items: center;
  padding: 12px 16px 12px 20px;
  border-top: 1px solid #eaecf0;
  background: #f9fafb;
}

.lesson-step-card__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.lesson-step-card--seen .lesson-step-card__status {
  color: #067647;
}
</style>
